import { useEffect, useRef, useState } from 'react';
import { Html5Qrcode } from 'html5-qrcode';
import { 
  CheckCircle, 
  XCircle, 
  WarningCircle, 
  ArrowLeft, 
  Camera, 
  ArrowsClockwise,
  Scan
} from '@phosphor-icons/react';
import { motion, AnimatePresence } from 'framer-motion';
import posthog from '../../../posthog';

type ScanState = 'scanning' | 'processing' | 'success' | 'already_scanned' | 'not_valid';

export function QRScannerView({ onBack }: { onBack?: () => void }) {
  const [scanState, setScanState] = useState<ScanState>('scanning');
  const [scanData, setScanData] = useState<string>('');
  const [isCameraActive, setIsCameraActive] = useState(false);
  const [errorMsg, setErrorMsg] = useState<string>('');
  
  const qrCodeScannerRef = useRef<Html5Qrcode | null>(null);
  const isScanningRef = useRef(false);

  useEffect(() => {
    // Initialize scanner
    qrCodeScannerRef.current = new Html5Qrcode("qr-reader");

    // Cleanup on unmount
    return () => {
      stopScanner();
    };
  }, []);

  const stopScanner = async () => {
    try {
      if (qrCodeScannerRef.current && isScanningRef.current) {
        await qrCodeScannerRef.current.stop();
        isScanningRef.current = false;
        setIsCameraActive(false);
      }
    } catch (err) {
      console.error("Failed to stop scanner", err);
    }
  };

  const startScanner = async () => {
    if (!qrCodeScannerRef.current) return;
    
    setErrorMsg('');
    setScanState('scanning');
    
    const config = {
      fps: 10,
      qrbox: { width: 250, height: 250 },
      aspectRatio: 1.0,
    };

    const onSuccess = async (decodedText: string) => {
      // Use the ref to prevent multiple scans instead of stale state closure
      if (isScanningRef.current) {
        isScanningRef.current = false;
        await stopScanner();
        handleScanSuccess(decodedText);
      }
    };

    const onError = () => {
      // Ignore frequent scan errors
    };
    
    try {
      await qrCodeScannerRef.current.start(
        { facingMode: "environment" },
        config,
        onSuccess,
        onError
      );
      isScanningRef.current = true;
      setIsCameraActive(true);
    } catch (err) {
      console.warn("Environment camera failed, trying user camera...", err);
      try {
        await qrCodeScannerRef.current.start(
          { facingMode: "user" },
          config,
          onSuccess,
          onError
        );
        isScanningRef.current = true;
        setIsCameraActive(true);
      } catch (fallbackErr) {
        console.error(fallbackErr);
        setErrorMsg('Failed to access camera. Please ensure permissions are granted or device has a camera.');
      }
    }
  };

  const handleScanSuccess = async (text: string) => {
    setScanState('processing');
    setScanData(text);

    // Mock API Verification Delay
    await new Promise(resolve => setTimeout(resolve, 800));

    // Mock Logic for states based on content
    const lowerText = text.toLowerCase();
    if (lowerText.includes('invalid') || lowerText.includes('fake')) {
      posthog.capture('qr_code_rejected', { rejection_reason: 'invalid' });
      setScanState('not_valid');
    } else if (lowerText.includes('scanned') || lowerText.includes('used')) {
      posthog.capture('qr_code_rejected', { rejection_reason: 'already_scanned' });
      setScanState('already_scanned');
    } else {
      posthog.capture('qr_code_verified');
      setScanState('success');
    }
  };

  const resetScanner = () => {
    setScanState('scanning');
    setScanData('');
    startScanner();
  };

  // Start scanner automatically on mount if we're in scanning state
  useEffect(() => {
    if (scanState === 'scanning' && !isCameraActive && qrCodeScannerRef.current) {
      startScanner();
    }
  }, [scanState]);

  return (
    <div className="min-h-screen w-full bg-app-bg md:p-6 lg:p-8 flex justify-center">
      <div className="w-full max-w-md bg-white md:rounded-2xl md:shadow-card overflow-hidden flex flex-col h-full md:h-auto min-h-screen md:min-h-[700px] relative">
        
        {/* Header */}
        <div className="px-5 py-4 border-b border-border flex items-center bg-white/95 backdrop-blur-md sticky top-0 z-10">
          {onBack && (
            <button 
              onClick={() => {
                stopScanner().then(onBack);
              }}
              className="mr-3 p-2 -ml-2 rounded-full hover:bg-slate-100 text-slate-500 transition-smooth"
            >
              <ArrowLeft size={20} weight="bold" />
            </button>
          )}
          <div>
            <h1 className="text-lg font-outfit font-bold text-slate-900 leading-tight">Scanner</h1>
            <p className="text-xs text-slate-500 font-medium">Scan code for quick access</p>
          </div>
        </div>

        {/* Content Area */}
        <div className="flex-1 flex flex-col items-center justify-center p-6 relative">
          
          <AnimatePresence mode="wait">
            
            {/* SCANNING STATE */}
            {(scanState === 'scanning' || scanState === 'processing') && (
              <motion.div 
                key="scanning"
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="w-full flex flex-col items-center"
              >
                <div className="relative w-full max-w-[300px] aspect-square rounded-[32px] overflow-hidden bg-slate-100 border-4 border-white shadow-card mb-6">
                  
                  {/* The actual video reader div */}
                  <div 
                    id="qr-reader" 
                    className="w-full h-full [&>video]:object-cover [&>video]:w-full [&>video]:h-full"
                  ></div>

                  {/* Overlay when processing */}
                  {scanState === 'processing' && (
                    <div className="absolute inset-0 bg-white/80 backdrop-blur-sm flex flex-col items-center justify-center z-10">
                      <div className="w-10 h-10 border-4 border-accent border-t-transparent rounded-full animate-spin mb-3"></div>
                      <p className="font-semibold text-slate-700 animate-pulse">Verifying...</p>
                    </div>
                  )}

                  {/* Fallback state when camera is inactive/loading */}
                  {!isCameraActive && scanState === 'scanning' && !errorMsg && (
                    <div className="absolute inset-0 flex flex-col items-center justify-center text-slate-400 bg-slate-50">
                      <Camera size={48} weight="duotone" className="mb-3 opacity-50" />
                      <p className="text-sm font-medium">Initializing camera...</p>
                    </div>
                  )}
                </div>

                {errorMsg ? (
                  <div className="text-center p-4 bg-rose-50 rounded-xl border border-rose-100 w-full mb-6">
                    <WarningCircle size={24} className="text-rose-500 mx-auto mb-2" />
                    <p className="text-sm text-rose-700">{errorMsg}</p>
                    <button 
                      onClick={startScanner}
                      className="mt-3 px-4 py-2 bg-white text-rose-600 text-sm font-semibold rounded-lg shadow-sm"
                    >
                      Try Again
                    </button>
                  </div>
                ) : (
                  <div className="text-center">
                    <div className="flex items-center justify-center gap-2 text-slate-600 mb-1">
                      <Scan size={20} />
                      <span className="font-semibold">Align code within frame</span>
                    </div>
                    <p className="text-sm text-slate-400">Scanning will start automatically</p>
                  </div>
                )}
              </motion.div>
            )}

            {/* SUCCESS STATE */}
            {scanState === 'success' && (
              <motion.div
                key="success"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="w-full flex flex-col items-center text-center px-4"
              >
                <div className="w-20 h-20 bg-emerald-100 text-emerald-500 rounded-full flex items-center justify-center mb-6 shadow-sm border-4 border-white">
                  <CheckCircle size={40} weight="fill" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900 mb-2 font-outfit">Verification Successful</h2>
                <p className="text-slate-500 mb-6 max-w-[260px]">
                  The code is valid and verified. Access has been granted.
                </p>
                <div className="w-full p-4 bg-slate-50 rounded-xl border border-border mb-8 break-all">
                  <p className="text-xs text-slate-400 font-medium mb-1 uppercase tracking-wider">Scanned Data</p>
                  <p className="text-sm font-semibold text-slate-700 font-mono">{scanData}</p>
                </div>
                <button 
                  onClick={resetScanner}
                  className="w-full btn-primary py-3.5 rounded-xl text-[15px] shadow-card-hover flex items-center justify-center gap-2"
                >
                  <Scan size={18} weight="bold" />
                  Scan Another Code
                </button>
              </motion.div>
            )}

            {/* ALREADY SCANNED STATE */}
            {scanState === 'already_scanned' && (
              <motion.div
                key="already"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="w-full flex flex-col items-center text-center px-4"
              >
                <div className="w-20 h-20 bg-amber-100 text-amber-500 rounded-full flex items-center justify-center mb-6 shadow-sm border-4 border-white">
                  <WarningCircle size={40} weight="fill" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900 mb-2 font-outfit">Already Scanned</h2>
                <p className="text-slate-500 mb-8 max-w-[260px]">
                  This code has already been used and cannot be scanned again.
                </p>
                <div className="w-full p-4 bg-slate-50 rounded-xl border border-border mb-8 break-all">
                  <p className="text-xs text-slate-400 font-medium mb-1 uppercase tracking-wider">Scanned Data</p>
                  <p className="text-sm font-semibold text-slate-700 font-mono">{scanData}</p>
                </div>
                <button 
                  onClick={resetScanner}
                  className="w-full btn-secondary py-3.5 rounded-xl text-[15px] shadow-sm flex items-center justify-center gap-2 bg-slate-50"
                >
                  <ArrowsClockwise size={18} weight="bold" />
                  Try Another Code
                </button>
              </motion.div>
            )}

            {/* NOT VALID STATE */}
            {scanState === 'not_valid' && (
              <motion.div
                key="invalid"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="w-full flex flex-col items-center text-center px-4"
              >
                <div className="w-20 h-20 bg-rose-100 text-rose-500 rounded-full flex items-center justify-center mb-6 shadow-sm border-4 border-white">
                  <XCircle size={40} weight="fill" />
                </div>
                <h2 className="text-2xl font-bold text-slate-900 mb-2 font-outfit">Invalid Code</h2>
                <p className="text-slate-500 mb-8 max-w-[260px]">
                  This QR code is not recognized by our system. Please check and try again.
                </p>
                <div className="w-full p-4 bg-slate-50 rounded-xl border border-border mb-8 break-all">
                  <p className="text-xs text-slate-400 font-medium mb-1 uppercase tracking-wider">Scanned Data</p>
                  <p className="text-sm font-semibold text-slate-700 font-mono">{scanData}</p>
                </div>
                <button 
                  onClick={resetScanner}
                  className="w-full btn-secondary py-3.5 rounded-xl text-[15px] shadow-sm flex items-center justify-center gap-2 bg-slate-50 border-rose-100 text-rose-600 hover:bg-rose-50 hover:text-rose-700"
                >
                  <ArrowsClockwise size={18} weight="bold" />
                  Scan Again
                </button>
              </motion.div>
            )}

          </AnimatePresence>
        </div>
      </div>
    </div>
  );
}
